import { useEffect, useState } from 'react'
import { Input, Skeleton } from 'antd'
import { useTranslation } from 'react-i18next'
import { Modal } from 'components/reusables/modal'
import { ModalButtons } from 'components/reusables/modalButtons'
import type { SurveyTemplateRow } from '../../common/types'
import { PICKER_PAGE_SIZE, TRANSLATION_PREFIX } from '../../constants'
import { useSurveyTemplates } from '../../hooks/useSurveyTemplates'
import {
  Option,
  OptionDescription,
  OptionMeta,
  OptionName,
  Options,
  PickerBody,
  Status,
} from './styled'

const SEARCH_DELAY_MS = 300

interface IProps {
  applying: boolean
  onConfirm: (row: SurveyTemplateRow) => void
  onClose: () => void
}

/** Picks an active survey of the bank to copy into a course survey. */
export const SurveyTemplatePicker = ({
  applying,
  onConfirm,
  onClose,
}: IProps) => {
  const { t } = useTranslation()
  const { rows, loading, setSearch } = useSurveyTemplates({
    status: 'active',
    perPage: PICKER_PAGE_SIZE,
  })
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<SurveyTemplateRow | null>(null)
  const prefix = `${TRANSLATION_PREFIX}.block.picker`

  useEffect(() => {
    const timeout = setTimeout(() => setSearch(query.trim()), SEARCH_DELAY_MS)
    return () => clearTimeout(timeout)
  }, [query, setSearch])

  const renderList = () => {
    if (loading) return <Skeleton active paragraph={{ rows: 3 }} />
    if (rows.length === 0)
      return (
        <Status data-testid="survey-block-picker-empty">
          {t(`${prefix}.noMatch`)}
        </Status>
      )
    return (
      <Options role="listbox" aria-label={t(`${prefix}.heading`)}>
        {rows.map((row) => {
          const isSelected = selected?.id === row.id
          return (
            <Option
              key={row.id}
              role="option"
              tabIndex={0}
              aria-selected={isSelected}
              $selected={isSelected}
              data-testid={`survey-block-picker-option-${row.id}`}
              onClick={() => setSelected(row)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  setSelected(row)
                }
              }}
            >
              <OptionName>{row.name}</OptionName>
              <OptionMeta>
                {t(`${prefix}.questionsCount`, { count: row.questionsCount })}
              </OptionMeta>
              {row.description && (
                <OptionDescription>{row.description}</OptionDescription>
              )}
            </Option>
          )
        })}
      </Options>
    )
  }

  return (
    <Modal
      title={t(`${prefix}.heading`)}
      close={onClose}
      maxwidth={640}
      maxheight={640}
      roboto
      actions={
        <ModalButtons
          secondaryText={t(`${prefix}.cancel`)}
          secondaryAction={onClose}
          primaryText={t(`${prefix}.use`)}
          primaryAction={() => selected && onConfirm(selected)}
          disablePrimaryButton={!selected}
          processing={applying}
        />
      }
    >
      <PickerBody data-testid="survey-block-picker">
        <Input
          data-testid="survey-block-picker-search"
          placeholder={t(`${prefix}.search`)}
          aria-label={t(`${prefix}.search`)}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          autoFocus
          allowClear
        />
        {renderList()}
      </PickerBody>
    </Modal>
  )
}
